import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectStatus } from 'app/pages/Home/slice/selectors';
import { RISK_LEVELS, normalizeRiskLevelToStored } from 'utils/constants';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data by Status (Risk Level)
 * Maps filter values to the shared RISK_LEVELS values:
 * - 'risk-level-low' → RISK_LEVELS.LOW
 * - 'risk-level-medium' → RISK_LEVELS.MODERATE
 * - 'risk-level-high' → RISK_LEVELS.HIGH
 * - 'in-planning' → RISK_LEVELS.IN_PLANNING
 * Filters on: moment.metaDetails.riskLevel, normalized via normalizeRiskLevelToStored
 * so label/color/case aliases (e.g. "Low" vs "green") match the same as elsewhere
 * in the app (see RiskLevelReadOnly, useFilteredParkingLot, Roadmap statusMatch).
 */
export const useFilterByStatus = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected status filters from Redux state
  const status = useSelector(selectStatus);
  const selectedStatus = status?.selectedStatus || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no status filters are selected, return original data
    if (selectedStatus.length === 0) {
      return milestonesData;
    }

    // Map filter values to the shared risk level constants
    const mapFilterToRiskLevel = (filterValue: string): string | null => {
      const mapping: Record<string, string> = {
        'risk-level-low': RISK_LEVELS.LOW,
        'risk-level-medium': RISK_LEVELS.MODERATE,
        'risk-level-high': RISK_LEVELS.HIGH,
        'in-planning': RISK_LEVELS.IN_PLANNING
      };
      return mapping[filterValue] || null;
    };

    // Convert selected filters to risk level values
    const targetRiskLevels = selectedStatus
      .map(filter => mapFilterToRiskLevel(filter))
      .filter(Boolean) as string[];

    // If no valid mappings found, return original data
    if (targetRiskLevels.length === 0) {
      return milestonesData;
    }

    // Filter the data based on selected status (risk level)
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      const riskLevel = normalizeRiskLevelToStored(
        moment.metaDetails?.riskLevel
      );

      // If moment has no risk level, exclude it
      if (!riskLevel) {
        return false;
      }

      // Check if the moment's risk level matches any of the target risk levels
      return targetRiskLevels.includes(riskLevel);
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedStatus]);

  return filteredData;
};
